import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {api} from "../api";
import {deleteLatestSession, GameDataParams} from "./adminGameView";
import {DataParams, NavigationParams, PlainTeam, Session, TeamResult} from "../../types/admin";

export type AdminGameSessionStatistics = {
    played: number;
    unique_users: number;
    average_points: number;
    max_points: number;
    average_duration: number;
}

export type AdminGameSessionState = {
    loaded: boolean,
    error: string | null,
    success: string | null,
    results: Session[],
    game_id: string | null,
    version_id: string | null
    count: number,
    next: string | null,
    previous: string | null,
    statistics: AdminGameSessionStatistics,
}

export const initialState: AdminGameSessionState = {
    loaded: false,
    error: null,
    success: null,
    results: [],
    game_id: null,
    version_id: null,
    count: 0,
    next: null,
    previous: null,
    statistics: {
        played: 0,
        unique_users: 0,
        average_points: 0,
        max_points: 0,
        average_duration: 0
    },
};

type CombinedParams = GameDataParams & DataParams;

const extractParams = (params: CombinedParams): NavigationParams => {
    let _params: any = {};
    if (params.page) _params.page = parseInt(params.page);
    if (params.sq) _params.search = params.sq;
    if (params.team) _params.team = params.team;
    if (params.passed) _params.passed = params.passed;
    if (params.company) _params.company = params.company;
    return _params;
}

export const deleteSession = createAsyncThunk(
    'adminGameSessions/deleteSession',
    async (session_id: string, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.delete(`/API/admin/sessions/${session_id}/`);
            dispatch(deleteLatestSession(response));
            return response
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getAdminGameSessions = createAsyncThunk(
    'adminGameSessions/getAdminGameSessions',
    async (params: CombinedParams, thunkAPI) => {
        const _params = extractParams(params);
        try {
            return await api.get(`/API/admin/games/${params.game_id}/versions/${params.version_id}/sessions/`, _params);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const slice = createSlice({
    name: "adminGameSessions",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getAdminGameSessions.pending, (state) => {
            state.loaded = false;
        });
        builder.addCase(getAdminGameSessions.fulfilled, (state, action) => {
            state.loaded = true;
            state.error = null;
            state.results = action.payload.results;
            state.count = action.payload.count;
            state.next = action.payload.next;
            state.previous = action.payload.previous;
            state.statistics = action.payload.statistics;
        });
        builder.addCase(getAdminGameSessions.rejected, (state, action) => {
            state.loaded = true;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(deleteSession.pending, (state) => {
            state.error = null;
        });
        builder.addCase(deleteSession.fulfilled, (state, action) => {
            state.error = null;
            // find session and remove it from results
            if(action.payload.session_id){
                state.results = state.results.filter(session => session.session_id !== action.payload.session_id);
            }
        });
        builder.addCase(deleteSession.rejected, (state, action) => {
            state.loaded = true;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
    }
});

export const {clearError} = slice.actions;
export default slice.reducer;
